
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { User, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import UniversitySearchBox from '@/components/auth/UniversitySearchBox';

const EnhancedProfileSection = () => {
  const { profile, updateProfile } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    language: profile?.language || 'ar',
    national_id: profile?.national_id || '',
    gender: profile?.gender || '',
    university_id: profile?.university_id || ''
  });

  // Update form data whenever profile changes
  React.useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        language: profile.language || 'ar',
        national_id: profile.national_id || '',
        gender: profile.gender || '',
        university_id: profile.university_id || ''
      });
    }
  }, [profile]);

  const handleSave = async () => {
    console.log('🔄 HandleSave called');
    console.log('📋 Current formData:', formData);
    
    // Check authentication first
    if (!profile?.id) {
      console.error('❌ No authenticated user found');
      toast.error('Please sign in to save your profile changes', {
        description: 'You need to be logged in to update your profile.',
        action: {
          label: 'Sign In',
          onClick: () => window.location.href = '/signin'
        }
      });
      return;
    }
    
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.full_name.trim()) {
        toast.error('Full name is required', {
          description: 'Please enter your full name before saving.'
        });
        return;
      }
      
      console.log('📤 Calling updateProfile with data:', {
        full_name: formData.full_name,
        phone: formData.phone,
        language: formData.language,
        national_id: formData.national_id,
        gender: formData.gender,
        university_id: formData.university_id || null
      });
      
      // Use the secure updateProfile function from AuthContext
      const result = await updateProfile({
        full_name: formData.full_name,
        phone: formData.phone,
        language: formData.language,
        national_id: formData.national_id,
        gender: formData.gender,
        university_id: formData.university_id || null
      });

      console.log('📥 UpdateProfile result:', result);

      if (result?.error) {
        console.error('❌ Profile update error:', result.error);
        toast.error('Failed to save changes', {
          description: `Error: ${result.error}`,
          duration: 5000
        });
        return;
      }

      console.log('✅ Profile update successful, exiting edit mode');
      setIsEditing(false);
      
      // Show success notification
      toast.success('Profile updated successfully!', {
        description: 'All your changes have been saved.',
        duration: 3000
      });
      
    } catch (error) {
      console.error('❌ Profile update exception:', error);
      toast.error('Unexpected error occurred', {
        description: 'Please try again or contact support if the issue persists.',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
      language: profile?.language || 'ar',
      national_id: profile?.national_id || '',
      gender: profile?.gender || '',
      university_id: profile?.university_id || ''
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    // Reset form data to current profile values when starting to edit
    setFormData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
      language: profile?.language || 'ar',
      national_id: profile?.national_id || '',
      gender: profile?.gender || '',
      university_id: profile?.university_id || ''
    });
    setIsEditing(true);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{t('profile_info', 'المعلومات الشخصية', 'Profile Information')}</CardTitle>
          </div>
          {!isEditing ? (
            <Button variant="outline" size="sm" onClick={handleEdit} className="self-start sm:self-auto">
              <Edit className="h-4 w-4 mr-2" />
              {t('edit', 'تعديل', 'Edit')}
            </Button>
          ) : (
            <div className="flex gap-2 self-start sm:self-auto">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={loading}>
                <X className="h-4 w-4 mr-2" />
                {t('cancel', 'إلغاء', 'Cancel')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                {loading ? t('saving', 'جاري الحفظ', 'Saving...') : t('save', 'حفظ', 'Save')}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Profile Information */}
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
            <div className="space-y-2">
              <Label htmlFor="name">{t('full_name', 'الاسم الكامل', 'Full Name')}</Label>
              {isEditing ? (
                <Input
                  id="name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder={t('enter_full_name', 'أدخل الاسم الكامل', 'Enter full name')}
                />
              ) : (
                <p className="font-medium text-sm px-3 py-2 bg-muted/50 rounded-md">{profile?.full_name || t('not_set', 'غير محدد', 'Not set')}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">{t('email', 'البريد الإلكتروني', 'Email')}</Label>
              <p className="font-medium text-sm px-3 py-2 bg-muted/50 rounded-md">{profile?.email}</p>
              <p className="text-xs text-muted-foreground">
                {t('email_readonly', 'لا يمكن تغيير البريد الإلكتروني', 'Email cannot be changed')}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">
                <div className="flex items-center gap-2">
                  {t('phone', 'رقم الهاتف', 'Phone Number')}
                  <span className="text-red-500">*</span>
                  <span className="text-xs text-muted-foreground">
                    {t('required_for_contact', '(مطلوب للتواصل)', '(Required for contact)')}
                  </span>
                </div>
              </Label>
              {isEditing ? (
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('enter_phone', 'أدخل رقم الهاتف', 'Enter phone number')}
                  className={!formData.phone ? 'border-red-200 bg-red-50' : ''}
                />
              ) : (
                <div className={`font-medium text-sm px-3 py-2 rounded-md ${
                  profile?.phone 
                    ? 'bg-muted/50' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {profile?.phone || t('phone_required_warning', 'رقم الهاتف مطلوب للتواصل مع المالكين', 'Phone number required to contact property owners')}
                </div>
              )}
              {!profile?.phone && !isEditing && (
                <p className="text-xs text-red-600">
                  {t('add_phone_to_contact', 'أضف رقم هاتفك للتواصل مع المالكين عبر واتساب', 'Add your phone number to contact property owners via WhatsApp')}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="national_id">
                <div className="flex items-center gap-2">
                  {t('national_id', 'الرقم القومي', 'National ID')}
                  <span className="text-red-500">*</span>
                  <span className="text-xs text-muted-foreground">
                    {t('required_for_verification', '(مطلوب للتحقق)', '(Required for verification)')}
                  </span>
                </div>
              </Label>
              {isEditing ? (
                <Input
                  id="national_id"
                  value={formData.national_id}
                  onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                  placeholder={t('enter_national_id', 'أدخل الرقم القومي', 'Enter national ID')}
                  className={!formData.national_id ? 'border-red-200 bg-red-50' : ''}
                />
              ) : (
                <div className={`font-medium text-sm px-3 py-2 rounded-md ${
                  profile?.national_id 
                    ? 'bg-muted/50' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {profile?.national_id || t('national_id_required_warning', 'الرقم القومي مطلوب للتحقق من الهوية', 'National ID required for identity verification')}
                </div>
              )}
              {!profile?.national_id && !isEditing && (
                <p className="text-xs text-red-600">
                  {t('add_national_id_security', 'أضف رقمك القومي للتحقق من الهوية وضمان الأمان', 'Add your national ID for identity verification and security')}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gender">{t('gender', 'الجنس', 'Gender')}</Label>
              {isEditing ? (
                <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_gender', 'اختر الجنس', 'Select gender')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('male', 'ذكر', 'Male')}</SelectItem>
                    <SelectItem value="female">{t('female', 'أنثى', 'Female')}</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium text-sm px-3 py-2 bg-muted/50 rounded-md">
                  {profile?.gender 
                    ? t(profile.gender === 'male' ? 'male' : 'female', profile.gender === 'male' ? 'ذكر' : 'أنثى', profile.gender === 'male' ? 'Male' : 'Female')
                    : t('not_set', 'غير محدد', 'Not set')
                  }
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="language">{t('language', 'اللغة', 'Language')}</Label>
              {isEditing ? (
                <Select value={formData.language} onValueChange={(value) => setFormData({ ...formData, language: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ar">{t('arabic', 'العربية', 'Arabic')}</SelectItem>
                    <SelectItem value="en">{t('english', 'الإنجليزية', 'English')}</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="font-medium text-sm px-3 py-2 bg-muted/50 rounded-md">
                  {profile?.language === 'ar' ? t('arabic', 'العربية', 'Arabic') : t('english', 'الإنجليزية', 'English')}
                </p>
              )}
            </div>
            
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="university">{t('university', 'الجامعة', 'University')}</Label>
              {isEditing ? (
                <UniversitySearchBox
                  value={formData.university_id}
                  onValueChange={(value) => setFormData({ ...formData, university_id: value })}
                />
              ) : (
                <p className="font-medium text-sm px-3 py-2 bg-muted/50 rounded-md">
                  {profile?.university_id ? t('university_selected', 'تم اختيار الجامعة', 'University selected') : t('not_set', 'غير محدد', 'Not set')}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedProfileSection;
